import { getLog } from '@eigtech/ui-shared-logging'
import { toast } from '@eigtech/ui/components/sonner'
import {
  useLocalVideo,
  useMeetingManager,
  useVideoInputs,
} from 'amazon-chime-sdk-component-library-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useLocalStorage } from 'react-use'
import { DeviceType } from '../components/Controls/DeviceSelection/DeviceInput'

const log = getLog()

export function useRememberCameraSelection() {
  const [defaultCameraDeviceLabel, setDefaultCameraDeviceLabel] = useLocalStorage<string>(
    'meeting-camera-selection'
  )
  const {
    devices,
    handleSelectCamera: baseHandleSelectCamera,
    selectedDevice,
  } = useCameraSelection()

  const handleSelectCamera = useCallback(
    async (deviceId: string) => {
      const device = await baseHandleSelectCamera(deviceId)
      if (device) {
        setDefaultCameraDeviceLabel(device.label)
      }
      return device
    },
    [baseHandleSelectCamera, setDefaultCameraDeviceLabel]
  )

  return {
    devices,
    selectedDevice,
    handleSelectCamera,
    defaultCameraDeviceLabel,
  }
}

export function useCameraSelection() {
  const meetingManager = useMeetingManager()
  const { devices, selectedDevice } = useVideoInputs()
  const { isVideoEnabled } = useLocalVideo()

  const handleSelectCamera = useCallback(
    async (deviceId: string) => {
      log.info('handleSelectCamera', {
        selectedDevice,
        deviceId,
        check: selectedDevice === deviceId,
      })
      if (selectedDevice === deviceId) return

      try {
        if (isVideoEnabled) {
          log.info('video enabled, switching deviceId')
          await meetingManager.startVideoInputDevice(deviceId)
        } else {
          log.info('video disabled, selecting deviceId')
          meetingManager.selectVideoInputDevice(deviceId)
        }

        const device = devices.find((device) => device.deviceId === deviceId)
        return device
      } catch (error) {
        log.error('CameraMenu failed to select camera', error)

        const errorMessage = error instanceof Error ? error.message : 'Unknown error'

        toast.error('Could not switch to selected camera', {
          description: errorMessage,
        })
      }
    },
    [devices, isVideoEnabled, meetingManager, selectedDevice]
  )

  return {
    devices,
    selectedDevice,
    handleSelectCamera,
  }
}

export function usePreselectRearCamera({
  devices,
  handleSelectCamera,
  defaultCameraDeviceLabel,
}: { defaultCameraDeviceLabel?: string } & Omit<
  ReturnType<typeof useCameraSelection>,
  'selectedDevice'
>) {
  const { isVideoEnabled } = useLocalVideo()
  const [state, setState] = useState<'pending' | 'initialized' | 'done'>('pending')
  const hasRun = useRef(false)

  useEffect(() => {
    if (hasRun.current) return

    if (isVideoEnabled) {
      log.warn('Cannot preselect rear camera when local video is already enabled!')
      return
    }

    if (!devices.length) {
      log.warn('Cannot preselect rear camera when there are no devices!')
      return
    }

    hasRun.current = true

    async function run() {
      log.info('preselecting rear camera')

      let deviceId = devices.find(({ label }) => label === defaultCameraDeviceLabel)?.deviceId
      if (!deviceId) {
        deviceId = await getRearFacingCamera(devices)
      }

      setState('initialized')

      if (deviceId) {
        await handleSelectCamera(deviceId)
      }

      setState('done')
    }

    run()
  }, [defaultCameraDeviceLabel, devices, handleSelectCamera, isVideoEnabled])

  return state
}

async function getRearFacingCamera(videoInputs: DeviceType[]): Promise<string | undefined> {
  log.info('setRearFacingCamera', JSON.stringify({ videoInputs }, null, 2))

  const deviceId = await getRearVideoInputDeviceId()
  if (deviceId) {
    log.info('found deviceId from constraints', deviceId)
    return deviceId
  }

  const rearCamera = videoInputs.find(
    (device) =>
      device.label.toLowerCase().includes('back') ||
      device.label.toLowerCase().includes('rear') ||
      device.label.toLowerCase().includes('environment')
  )
  if (rearCamera) {
    log.info('found rear camera from label', JSON.stringify({ rearCamera }, null, 2))
    return rearCamera.deviceId
  }

  log.info('rear camera device not found')
  return undefined
}

async function getRearVideoInputDeviceId() {
  const constraints = {
    video: {
      facingMode: {
        ideal: 'environment', // Prefers the back camera
      },
    },
  }
  log.info('video constraints', JSON.stringify(constraints, null, 2))

  const stream = await navigator.mediaDevices
    .getUserMedia(constraints)
    .catch((e) => log.error('Could not get user media', { error: e }))

  if (!stream) return

  const tracks = stream.getVideoTracks()
  log.info('video tracks from constraint', JSON.stringify(tracks, null, 2))

  let deviceId: string | undefined

  if (tracks.length) {
    const [track] = tracks
    log.info(`using trackId "${track.getSettings().deviceId}"`)

    deviceId = track.getSettings().deviceId
  }

  stream.getTracks().forEach((track) => track.stop())

  return deviceId
}
